/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { ColumnComponent, ColumnVisibilityChangeEvent } from '@progress/kendo-angular-grid';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { Logger } from '@app/core';
import { Workbook } from '@progress/kendo-angular-excel-export';
import { isArray } from 'lodash';
import { DateTime } from '@app/shared/models/date-time.model';
import * as moment from 'moment';
import { environment } from '@env/environment';
import { saveAs } from 'file-saver';

const log = new Logger('GridSettings');

export interface GridSettings {
  columns: ColumnSetting[];
  // state: State;
  // gridData?: DataResult;
}

export interface ColumnSetting {
  hidden?: boolean;
  field: string;
  subName?: string;
  // title?: string;
  // filter?: 'string' | 'numeric' | 'date' | 'boolean';
  // format?: string;
  // width?: number;
  // _width?: number;
  // filterable: boolean;
  // orderIndex?: number;
}

export class KendoGridComponent {
  /**
   * @var GridSettings
   */
  protected gridSettings: GridSettings = { columns: [] };

  /**
   * @param kendoGridService Kendo grid service
   */
  constructor(protected kendoGridService: KendoGridService) {
    const gridSettings: GridSettings = this.kendoGridService.get(this.constructor.name);
    log.info('const set', gridSettings);
    if (gridSettings !== null) {
      this.gridSettings = this.mapGridSettings(gridSettings);
    }
  }

  /**
   * Visibility event change
   * @param e ColumnVisibilityChangeEvent
   * @param subName Sub name of controller
   */
  public onVisibilityChange(e: ColumnVisibilityChangeEvent, subName: string = null): void {
    let hasChanges = false;

    e.columns.forEach((column: ColumnComponent) => {
      // A fieldless column cannot be found by isHiddenColumn after a reload.
      if (!column.field) {
        return;
      }

      const item: ColumnSetting = this.getKendoGridColumnSetting(column.field, subName);
      if (item) {
        item.hidden = column.hidden;
      } else {
        this.gridSettings.columns.push({
          field: column.field,
          hidden: column.hidden,
          subName: subName,
        });
      }
      hasChanges = true;
    });

    if (hasChanges) {
      this.saveKendoGrid();
    }
  }

  /**
   * Is hidden column
   * @param name Column name
   * @param defaultIsHidden Default hidden state
   * @param subName Sub name of field
   */
  public isHiddenColumn(name: string, defaultIsHidden: boolean = false, subName: string = null): boolean {
    const columnSetting = this.getKendoGridColumnSetting(name, subName);
    if (columnSetting) {
      return columnSetting.hidden;
    }
    return defaultIsHidden;
  }

  /**
   *  Transform cells for excel export
   * @param args object to export
   * @param fileName fileName
   * @param grid grid
   * @param colOffset Offset на колоните, използва се ако има колони без field в началото(като N, чекбокс)
   */
  public onExcelExport(args: any, fileName: string, grid: any, colOffset: number = 1) {
    args.preventDefault();
    grid.loading = true;
    const workbook = args.workbook;
    log.debug('args', args);

    args.workbook.sheets[0].rows.forEach((row: any, rowIndex: number) => {
      if (row.type === 'data') {
        row.cells.forEach((cell: any, cellIndex: number) => {
          if (cell.value === undefined || cell.value === null || isArray(cell.value)) {
            cell.value = grid.wrapper.nativeElement
              .querySelectorAll('.k-grid-aria-root kendo-grid-list .k-grid-table tbody tr')
              [rowIndex - 1].querySelectorAll('td')
              [cellIndex + colOffset].textContent.trim()
              .replace(/(\r\n|\r|\n|\t|[ ]{2,})/gimu, ',')
              .replace(/[,]{2,}/gimu, ', ');
          } else if (cell.value instanceof DateTime) {
            cell.value = cell.value.getDateTime();
          } else if (cell.value && cell.value.length >= 10 && moment(cell.value, moment.ISO_8601, true).isValid()) {
            cell.value = moment(cell.value, moment.ISO_8601, true).format(environment.momentDateFormat);
          } else if (cell.value === false || cell.value === true) {
            cell.value = cell.value.toString().trim();
          }
        });
      }
    });

    return new Workbook(workbook).toDataURL().then(
      (dataUrl: string) => {
        saveAs(dataUrl, fileName + '.xlsx');
        grid.loading = false;
      },
      () => {
        grid.loading = false;
      }
    );
  }

  /*
  Като onExcelExport за файнълите, структурата на грида е различна. Не искам да счупя onExcelExport за това правя тази функция
  Работи по същия начин
   */
  public onExcelExportFinals(args: any, fileName: string, grid: any, colOffset: number = 1): void {
    args.preventDefault();
    grid.loading = true;
    const workbook = args.workbook;
    log.debug('args', args);

    args.workbook.sheets[0].rows.forEach((row: any, rowIndex: number) => {
      if (row.type === 'data') {
        row.cells.forEach((cell: any, cellIndex: number) => {
          if (cell.value === undefined || cell.value === null || isArray(cell.value)) {
            cell.value = grid.wrapper.nativeElement
              .querySelectorAll('.k-grid-aria-root tbody tr')
              [rowIndex - 1].querySelectorAll('td')
              [cellIndex + colOffset].textContent.trim()
              .replace(/(\r\n|\r|\n|\t|[ ]{2,})/gimu, ',')
              .replace(/[,]{2,}/gimu, ', ');
          } else if (cell.value instanceof DateTime) {
            cell.value = cell.value.getDateTime();
          } else if (cell.value && cell.value.length >= 10 && moment(cell.value, moment.ISO_8601, true).isValid()) {
            cell.value = moment(cell.value, moment.ISO_8601, true).format(environment.momentDateFormat);
          } else if (cell.value === false || cell.value === true) {
            cell.value = cell.value.toString().trim();
          }
        });
      }
    });

    new Workbook(workbook).toDataURL().then(
      (dataUrl: string) => {
        saveAs(dataUrl, fileName + '.xlsx');
        grid.loading = false;
      },
      () => {
        grid.loading = false;
      }
    );
  }

  /**
   * Map setting data
   * @param gridSettings GridSetting
   */
  private mapGridSettings(gridSettings: GridSettings): GridSettings {
    return {
      // state,
      columns: gridSettings.columns ? gridSettings.columns : [],
      // gridData: process(sampleProducts, state)
    };
  }

  /**
   * Get column setting
   * @param field Column name
   * @param subName Sub name of column
   */
  private getKendoGridColumnSetting(field: string, subName: string = null) {
    if (!this.gridSettings.columns) {
      return null;
    }
    return this.gridSettings.columns.find((col) => col.field === field && col.subName === subName);
  }

  /**
   * Save setting to API
   */
  private saveKendoGrid(): void {
    this.kendoGridService.set(this.constructor.name, this.gridSettings);
  }
}
